import { useEffect, useState } from 'react';

import { ACCEPT_TYPE_IMAGE } from 'lib/form/file-dropper/file-dropper.constants';
import { useFileDropperContext } from 'lib/form/file-dropper/file-dropper';
import { FileDropperImage } from 'lib/form/file-dropper/file-dropper-image';
import { ComponentActions } from '../../components.style';
import ComponentImageModal from '../../shared/component-image-modal/component-image-modal';
import ComponentAltTextButton from '../../shared/component-alt-text/component-alt-text-button';
import { useMultipleChoiceUpdate } from '../settings/multiple-choice-settings.hooks';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import { EditProps } from '../multiple-choice.constants';
import { updateOptionImageData } from '../settings/multiple-choice-settings.utils';
import {
  canShowOptionLetter,
  getImageCropData,
  getOptionsPerColumn,
} from '../multiple-choice.utils';
import { MultipleChoiceOption } from '../multiple-choice.constants';
import { MultipleChoiceLetterImage } from '../multiple-choice';
import MultipleChoiceImage from './multiple-choice-image';
import { MultipleChoiceEditBox } from '../multiple-choice-edit.style';
import * as Styled from './multiple-choice-edit-image.style';
import { LANGUAGE_CODE_EN_AU } from 'global/constants';

type Props = EditProps & {
  index: number;
  isUpdating: boolean;
  option: MultipleChoiceOption;
  setIsUpdating: (state: boolean) => void;
};

const MultipleChoiceEditImage = ({
  properties,
  subActivityProperties,
  suggestedAnswerProperties,
  sectionId,
  id,
  type,
  sort,
  index,
  isUpdating,
  option,
  version,
  setIsUpdating,
}: Props) => {
  const uploadKey = `image-${index}`;

  const [elementRef, setRef] = useState<HTMLDivElement | null>(null);

  const { file, clearFile } = useFileDropperContext(uploadKey);
  const { isVisible, openModal, closeModal } = useSimpleModalState();

  const options = properties.multipleChoice.options;

  const { loading, handleUpdate, handleFileUpload } = useMultipleChoiceUpdate({
    id,
    sectionId,
    type,
    sort,
    language: LANGUAGE_CODE_EN_AU,
    version,
    properties,
    subActivityProperties,
    suggestedAnswerProperties,
  });

  useEffect(() => {
    if (file) {
      setIsUpdating(true);
    }
  }, [file, setIsUpdating]);

  return (
    <MultipleChoiceEditBox
      $itemsPerRow={getOptionsPerColumn(properties.multipleChoice)}
      $minHeight={properties.multipleChoice.optionMinHeight || 80}
      ref={setRef}
      id={`multi-choice-${option.itemId}`}
    >
      {isVisible && elementRef && (
        <ComponentImageModal
          loading={loading}
          containerDimensions={{
            width: elementRef.offsetWidth,
            height: elementRef.offsetHeight,
          }}
          image={option.image || undefined}
          altText={option.image?.alt}
          sourceUrl={option.sourceUrl}
          cropData={getImageCropData(option.imageLayout, option.cropData)}
          closeModal={closeModal}
          onUpdateData={data =>
            handleUpdate(
              {
                ...properties,
                multipleChoice: {
                  ...properties.multipleChoice,
                  options: updateOptionImageData({
                    options,
                    id: option.itemId,
                    altText: data.altText,
                    cropData: data.cropData,
                    imageLayout: data.imageLayout,
                    image: data.image || null,
                    sourceUrl: data.sourceUrl,
                  }),
                },
              },
              {
                isSaveImmediate: true,
                onCompleted: () => {
                  setIsUpdating(false);
                  clearFile();
                  closeModal();
                },
              }
            )
          }
        />
      )}
      {canShowOptionLetter(properties.multipleChoice.showOptionLetter) && (
        <MultipleChoiceLetterImage index={index} />
      )}
      {option.image === null ? (
        <Styled.MultipleChoiceEditImageUpload>
          <FileDropperImage
            accept={[ACCEPT_TYPE_IMAGE]}
            hidePlaceholder
            showAcceptExtensions={false}
            disabled={isUpdating}
            name={uploadKey}
            additionalLabel={
              elementRef
                ? `${elementRef.offsetWidth * 2}px x ${
                    elementRef.offsetHeight * 2
                  }px (W x H)`
                : '...'
            }
            onChange={(key, fileName) => {
              if (option.itemId) {
                handleFileUpload({
                  id: option.itemId,
                  key,
                  fileName,
                  properties,
                  onCompleted: () => {
                    clearFile();
                    setIsUpdating(false);
                  },
                });
              }
            }}
            onFileRemoved={() => setIsUpdating(false)}
          />
        </Styled.MultipleChoiceEditImageUpload>
      ) : (
        <Styled.MultipleChoiceEditImage>
          <Styled.MultipleChoiceEditImageButton
            type="button"
            aria-haspopup="true"
            aria-label={`Click to edit ${option.image.alt} image`}
            onClick={openModal}
          >
            <MultipleChoiceImage option={option} />
          </Styled.MultipleChoiceEditImageButton>
          <ComponentActions>
            <ComponentAltTextButton
              hasAltText={!!option.image.alt}
              onClick={openModal}
            />
          </ComponentActions>
        </Styled.MultipleChoiceEditImage>
      )}
    </MultipleChoiceEditBox>
  );
};

export default MultipleChoiceEditImage;
